/**
 * @file     AudienceSwitcher.tsx
 * @readme   ./README.md
 * @purpose  Horizontal tabs to switch between Buyer / Seller / Landlord journeys.
 */
'use client';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Link from 'next/link';
import { useAppTheme } from '@/core/theme';
import { AUDIENCE_LABELS, type AudienceId } from './types';

interface AudienceSwitcherProps {
  activeAudience: AudienceId;
}

const audiences: AudienceId[] = ['buyer', 'seller', 'landlord'];

export function AudienceSwitcher({ activeAudience }: AudienceSwitcherProps) {
  const { theme } = useAppTheme();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {audiences.map((id) => {
        const isActive = id === activeAudience;
        return (
          <Button
            key={id}
            component={Link}
            href={`/learn-about-bc-real-estate/${id}`}
            variant={isActive ? 'contained' : 'outlined'}
            size="small"
            sx={{
              textTransform: 'none',
              borderRadius: `${theme.layout.radius.md}px`,
              fontWeight: 500,
              fontSize: 13,
              px: 2.5,
              py: 0.8,
              transition: `all ${dur.fast}ms ${ease.standard}`,
              ...(isActive ? {} : {
                borderColor: theme.semanticColors.borderSubtle,
                color: theme.semanticColors.textSecondary,
                '&:hover': {
                  backgroundColor: alpha(theme.rawSemanticColors.interactivePrimary, 0.06),
                  borderColor: theme.semanticColors.interactivePrimary,
                },
              }),
            }}
          >
            {AUDIENCE_LABELS[id].labelDefault}
          </Button>
        );
      })}
    </Box>
  );
}
